import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Wrench, Users, FileText, DollarSign, Plus, CreditCard } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { PageHeader, Card, Badge } from '../common';
import { jobAPI } from '../../services/api';
import { useSubscription } from '../../hooks/useSubscription';
import logger from '../../utils/logger';

export default function MechanicHome() {
  const { user } = useAuth();
  const { subscription, subscriptionLoading, getTierDisplayName } = useSubscription();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // The client method is getDashboardStats. This called getJobStats,
      // which does not exist on jobAPI — so every render threw a TypeError
      // into the catch below and all four cards showed their '—' fallback.
      const data = await jobAPI.getDashboardStats();
      setStats(data);
    } catch (err) {
      // Stats are non-critical to the page rendering, so this stays
      // non-fatal — but it is logged rather than swallowed. Silence is what
      // let a missing method go unnoticed here.
      logger.error('Failed to load mechanic dashboard stats:', err);
    }
  };

  const statCards = [
    {
      label: 'Open Jobs',
      value: stats?.active_jobs ?? '—',
      icon: Wrench,
      color: 'text-ink-soft',
      bg: 'bg-surface-sunken',
    },
    {
      label: 'Customers',
      value: stats?.total_customers ?? '—',
      icon: Users,
      color: 'text-ink-soft',
      bg: 'bg-surface-sunken',
    },
    {
      label: 'Pending Invoices',
      value: stats?.pending_invoices ?? '—',
      icon: FileText,
      color: 'text-ink-soft',
      bg: 'bg-surface-sunken',
    },
    {
      label: 'Revenue (Month)',
      value: stats?.monthly_revenue != null ? `$${Number(stats.monthly_revenue).toLocaleString()}` : '—',
      icon: DollarSign,
      color: 'text-ink-soft',
      bg: 'bg-surface-sunken',
    },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title={`Welcome, ${user?.full_name || 'User'}`}
        subtitle="Manage jobs, customers, and invoices from your dashboard."
        action={
          <Link
            to="/jobs"
            className="inline-flex items-center gap-2 min-h-touch px-4 rounded-lg
              bg-accent text-white font-medium text-body
              hover:bg-accent-hover transition-colors"
          >
            <Plus size={20} />
            New Job
          </Link>
        }
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map(({ label, value, icon: Icon, color, bg }) => (
          <Card key={label}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${bg}`}>
                <Icon className={`h-5 w-5 ${color}`} strokeWidth={1.5} aria-hidden="true" />
              </div>
              <div>
                <p className="text-page-title font-mono tabular-nums text-ink">
                  {value}
                </p>
                <p className="text-small text-ink-muted">
                  {label}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to="/jobs" className="block group">
          <Card>
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-lg bg-surface-sunken">
                <Wrench className="h-5 w-5 text-ink-soft" strokeWidth={1.5} aria-hidden="true" />
              </div>
              <h3 className="ml-3 text-card-title font-display text-ink">
                Jobs
              </h3>
            </div>
            <p className="text-body mb-4 text-ink-muted">
              Create and manage diagnostic jobs, run AI analysis.
            </p>
            <span className="text-body-strong text-accent">Manage Jobs →</span>
          </Card>
        </Link>

        <Link to="/customers" className="block group">
          <Card>
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-lg bg-surface-sunken">
                <Users className="h-5 w-5 text-ink-soft" strokeWidth={1.5} aria-hidden="true" />
              </div>
              <h3 className="ml-3 text-card-title font-display text-ink">
                Customers
              </h3>
            </div>
            <p className="text-body mb-4 text-ink-muted">
              Manage your customer database and communications.
            </p>
            <span className="text-body-strong text-accent">View Customers →</span>
          </Card>
        </Link>

        <Link to="/invoices" className="block group">
          <Card>
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-lg bg-surface-sunken">
                <FileText className="h-5 w-5 text-ink-soft" strokeWidth={1.5} aria-hidden="true" />
              </div>
              <h3 className="ml-3 text-card-title font-display text-ink">
                Invoices
              </h3>
            </div>
            <p className="text-body mb-4 text-ink-muted">
              Create, send, and track invoices for your services.
            </p>
            <span className="text-body-strong text-accent">View Invoices →</span>
          </Card>
        </Link>
      </div>
    </div>
  );
}
